/**
 * Saved places (localStorage, this browser only): add / remove / rename, change events, import /
 * export. The list UI and the comparison live in saved.js (loaded on demand).
 */
import { bus } from './state.js';

const KEY = 'qh.saved.v1';
const MAX = 300;
const NEAR = 3e-6; // ~0.3 m: the same point

const num = (v) => Number.isFinite(+v) ? +v : NaN;
const str = (v, n) => (typeof v === 'string' ? v.trim().slice(0, n) : '');

function sanitize(p) {
  const lat = num(p?.lat), lng = num(p?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  const pl = p.plan && typeof p.plan === 'object' ? p.plan : null;
  return {
    id: /^[a-z0-9]{6,16}$/.test(p.id || '') ? p.id : newId(),
    name: str(p.name, 120) || 'Địa điểm',
    note: str(p.note, 500),
    lat: +lat.toFixed(6), lng: +lng.toFixed(6),
    addr: str(p.addr, 200),
    plan: pl ? { fn: str(pl.fn, 160) || null, code: str(pl.code, 40) || null, color: /^#[0-9a-f]{3,8}$/i.test(pl.color || '') ? pl.color : null, level: str(pl.level, 80) || null } : null,
    t: Number.isFinite(p.t) ? p.t : Date.now(),
  };
}

const newId = () => Date.now().toString(36).slice(-5) + Math.random().toString(36).slice(2, 7);

let cache = null;
export function savedList() {
  if (cache) return cache;
  try {
    const a = JSON.parse(localStorage.getItem(KEY) || '[]');
    cache = Array.isArray(a) ? a.map(sanitize).filter(Boolean) : [];
  } catch { cache = []; }
  return cache;
}

function persist(list) {
  cache = list;
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
  bus.emit('saved', list);
}

// another tab changed the list
if (typeof addEventListener === 'function') addEventListener('storage', (e) => { if (e.key === KEY) { cache = null; bus.emit('saved', savedList()); } });

export const findSaved = (lat, lng) => savedList().find((p) => Math.abs(p.lat - lat) < NEAR && Math.abs(p.lng - lng) < NEAR) || null;
export const savedById = (id) => savedList().find((p) => p.id === id) || null;

/** Save (or refresh) a place; returns the stored record. */
export function addSaved(p) {
  const rec = sanitize(p);
  if (!rec) return null;
  const list = savedList().filter((x) => !(Math.abs(x.lat - rec.lat) < NEAR && Math.abs(x.lng - rec.lng) < NEAR));
  list.unshift(rec);
  persist(list.slice(0, MAX));
  return rec;
}

export function updateSaved(id, patch) {
  const list = savedList().map((p) => (p.id === id ? sanitize({ ...p, ...patch, id }) || p : p));
  persist(list);
}

export function removeSaved(id) {
  const list = savedList();
  const i = list.findIndex((p) => p.id === id);
  if (i < 0) return null;
  const [gone] = list.splice(i, 1);
  persist([...list]);
  return { rec: gone, index: i };
}

/** Put a removed place back where it was (undo). */
export function restoreSaved({ rec, index }) {
  const list = savedList().filter((p) => p.id !== rec.id);
  list.splice(Math.min(index, list.length), 0, rec);
  persist(list.slice(0, MAX));
}

export function reorderSaved(ids) {
  const by = new Map(savedList().map((p) => [p.id, p]));
  persist([...ids.map((id) => by.get(id)).filter(Boolean), ...savedList().filter((p) => !ids.includes(p.id))]);
}

/** Merge imported places (JSON array or GeoJSON); returns { added, skipped }. */
export function importSaved(data) {
  let items = [];
  if (Array.isArray(data)) items = data;
  else if (data?.type === 'FeatureCollection') {
    items = (data.features || []).filter((f) => f?.geometry?.type === 'Point').map((f) => ({
      ...(f.properties || {}), lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1],
      name: f.properties?.name || f.properties?.title, plan: f.properties?.plan || null,
    }));
  } else if (Array.isArray(data?.places)) items = data.places;
  const list = savedList();
  let added = 0, skipped = 0;
  for (const raw of items) {
    const rec = sanitize({ ...raw, id: undefined });
    if (!rec || list.some((x) => Math.abs(x.lat - rec.lat) < NEAR && Math.abs(x.lng - rec.lng) < NEAR)) { skipped++; continue; }
    list.push(rec);
    added++;
  }
  persist(list.slice(0, MAX));
  return { added, skipped };
}

export function savedGeoJSON(list = savedList()) {
  return {
    type: 'FeatureCollection',
    features: list.map((p) => ({ type: 'Feature', properties: { id: p.id, name: p.name, note: p.note || undefined, addr: p.addr || undefined, plan: p.plan || undefined, saved: new Date(p.t).toISOString() }, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
  };
}
